<div class="fm-rs-steps">

  <!-- Page Title -->
  <div class="fm-page-title">
    <h1 class="fm-head-24 fm-title">Answer a Prompt</h1>
  </div>

  <!-- ====================================================================== -->
  <!-- VALIDATING STATE                                                        -->
  <!-- ====================================================================== -->
  @if (isValidating()) {
    <div class="fm-card-wrapper">
      <div class="fm-white-column text-center py-5">
        <div class="spinner-border fm-publishing-spinner mb-3" role="status" aria-hidden="true"></div>
        <p class="fm-large mb-0">Verifying your recording link…</p>
      </div>
    </div>
  }

  <!-- ====================================================================== -->
  <!-- INVALID / ERROR STATE                                                   -->
  <!-- ====================================================================== -->
  @if (!isValidating() && linkInvalid()) {
    <div class="fm-card-wrapper">
      <div class="fm-white-column text-center py-5">
        <div class="fm-reload-modal-icon mb-3">
          <span>&#9888;</span>
        </div>
        <h2 class="fm-head-24 mb-3">{{ linkErrorTitle() }}</h2>
        <p class="fm-large mb-0">{{ linkError() }}</p>
      </div>
    </div>
  }

  <!-- ====================================================================== -->
  <!-- RECORDING STATE                                                         -->
  <!-- ====================================================================== -->
  @if (!isValidating() && !linkInvalid()) {
    <div class="fm-card-wrapper">
      <div class="row g-4">

        <!-- Main Recording Area -->
        <div class="col-lg-8">
          <div class="fm-recording-wrapper">

            <!-- Prompt Title -->
            <div class="fm-white-column mb-4">
              <p class="fm-small text-muted mb-1">Prompt</p>
              <h2 class="fm-head-20 mb-0">{{ promptTitle() }}</h2>
            </div>

            <!-- Progress (multi-question links only) -->
            @if (questionsList().length > 1) {
              <div class="fm-progress-wrapper">
                @if (allAnsweredByUpload()) {
                  <p class="fm-que-txt">All questions answered by your uploaded file</p>
                } @else {
                  <p class="fm-que-txt">Question {{ currentQuestionIndex() + 1 }} of {{ questionsList().length }}</p>
                }
                <p class="fm-pcompleted-count">{{ progressPercent() }}% Completed</p>
              </div>
              <div class="progress fm-progress-bar" role="progressbar"
                   [attr.aria-valuenow]="progressPercent()" aria-valuemin="0" aria-valuemax="100">
                <div class="progress-bar" [style.width.%]="progressPercent()"></div>
              </div>
            }

            <!-- Question Box -->
            <div class="fm-question-box">
              <p class="mb-2">Your Question</p>
              <h4 class="fm-head-20 text-break">{{ currentQuestion()?.questionText }}</h4>
            </div>

            <!-- ============================================================ -->
            <!-- VIDEO RECORDING                                               -->
            <!-- ============================================================ -->
            @if (mediaTypeStr() === 'video') {
              <div class="fm-video-box">
                <div class="fm-video-preview" [attr.data-recording-state]="recordingState()">
                  @if (recordingState() === 'idle' && !recordedUrl() && !uploadedUrl()) {
                    <div class="fm-video-icon">
                      <img src="images/icons/fm-video-lg.svg" class="fm-svg" alt="Video Preview" />
                    </div>
                    <p class="fm-record-status mb-0">Ready to record</p>
                  }
                  <video #videoPreview class="fm-video-element"
                         [class.d-none]="recordingState() === 'idle' && !recordedUrl() && !uploadedUrl()"
                         autoplay playsinline
                         disablepictureinpicture
                         disableremoteplayback
                         controlslist="nodownload noplaybackrate"></video>
                  @if (recordingState() === 'recording') {
                    <div class="fm-recording-indicator" aria-live="polite">
                      <span class="fm-rec-dot" aria-hidden="true"></span>
                      <span class="fm-rec-time">{{ formatRecordingTime(recordingTimer()) }}</span>
                    </div>
                  }
                </div>
                <div class="fm-video-btn">
                  <div class="row g-3 g-xl-4">
                    @if (recordingState() !== 'recording') {
                      <div class="col-sm-6">
                        <button type="button" class="fm-common-btn fm-record-btn fm-large-btn w-100"
                                (click)="startVideoRecording()">
                          <img src="images/icons/fm-record.svg" class="fm-svg" alt="Record" />
                          {{ (recordedUrl() || uploadedUrl()) ? 'Re-record Video' : 'Record Video' }}
                        </button>
                      </div>
                      <div class="col-sm-6">
                        <label for="spVideoUploadInput"
                               class="fm-common-btn fm-secondary-btn fm-secondary-border-btn fm-upload-btn fm-large-btn w-100"
                               style="cursor:pointer">
                          <img src="images/icons/fm-upload.svg" class="fm-svg" alt="Upload" />
                          Upload Video
                        </label>
                        <input type="file" id="spVideoUploadInput" class="d-none" accept="video/*"
                               (change)="onVideoUpload($event)" />
                        @if (uploadErrorMsg()) {
                          <div class="fm-upload-error" role="alert">
                            <span>&#9888;</span> {{ uploadErrorMsg() }}
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="col-sm-6">
                        <button type="button" class="fm-common-btn fm-secondary-btn fm-secondary-border-btn fm-large-btn w-100"
                                (click)="retakeRecording()">
                          <img src="images/icons/fm-reset.svg" class="fm-svg" alt="Retake" />
                          Retake
                        </button>
                      </div>
                      <div class="col-sm-6">
                        <button type="button" class="fm-common-btn fm-record-btn fm-large-btn w-100 fm-btn-recording"
                                (click)="stopRecording()">
                          <img src="images/icons/fm-recording-dot.svg" class="fm-svg" alt="Stop" />
                          Stop Recording
                        </button>
                      </div>
                    }
                  </div>
                </div>
                @if (permissionError() && mediaTypeStr() === 'video') {
                  <div class="alert alert-danger mt-3 mb-0" role="alert">
                    <strong>{{ permissionError() }}</strong>
                    @if (isPermissionDenied()) {
                      <div class="mt-2">
                        Set Camera &amp; Microphone to <strong>Allow</strong>, then reload the page and try again.
                      </div>
                    }
                  </div>
                }
              </div>
            }

            <!-- ============================================================ -->
            <!-- AUDIO RECORDING                                               -->
            <!-- ============================================================ -->
            @if (mediaTypeStr() === 'audio') {
              <div class="fm-audio-box">
                <div class="fm-audio-preview" [attr.data-recording-state]="recordingState()"
                     [class.fm-drag-over]="audioDragOver()"
                     (dragover)="onAudioDragOver($event)"
                     (dragleave)="onAudioDragLeave($event)"
                     (drop)="onAudioDrop($event)">
                  @if (recordingState() !== 'recording') {
                    <div class="fm-audio-icon">
                      <img src="images/icons/fm-micro-icon.svg" class="fm-svg" alt="Audio" />
                    </div>
                    @if (!recordedUrl() && !uploadedUrl()) {
                      <p class="fm-drop-hint mb-0 mt-2">Drop audio file here or use buttons below</p>
                    }
                  }
                  @if (recordingState() === 'recording') {
                    <div class="fm-audio-visualizer" aria-hidden="true">
                      <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                      <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                      <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                      <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                      <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                      <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                    </div>
                    <div class="fm-audio-rec-row">
                      <span class="fm-rec-dot" aria-hidden="true"></span>
                      <p class="fm-record-status mb-0" aria-live="polite">
                        Recording... {{ formatRecordingTime(recordingTimer()) }}
                      </p>
                    </div>
                  }
                  <audio #audioPreview class="fm-audio-player w-100 mt-3"
                         [class.d-none]="recordingState() !== 'stopped' || (!recordedUrl() && !uploadedUrl())"
                         controls></audio>
                </div>
                <div class="fm-audio-btn">
                  <div class="row g-3 g-xl-4">
                    @if (recordingState() !== 'recording') {
                      <div class="col-sm-6">
                        <button type="button" class="fm-common-btn fm-record-btn fm-large-btn w-100"
                                (click)="startAudioRecording()">
                          <img src="images/icons/fm-record.svg" class="fm-svg" alt="Record" />
                          {{ (recordedUrl() || uploadedUrl()) ? 'Re-record Audio' : 'Record Audio' }}
                        </button>
                      </div>
                      <div class="col-sm-6">
                        <label for="spAudioUploadInput"
                               class="fm-common-btn fm-secondary-btn fm-secondary-border-btn fm-upload-btn fm-large-btn w-100"
                               style="cursor:pointer">
                          <img src="images/icons/fm-upload.svg" class="fm-svg" alt="Upload" />
                          Upload Audio
                        </label>
                        <input type="file" id="spAudioUploadInput" class="d-none" accept="audio/*"
                               (change)="onAudioUpload($event)" />
                        @if (audioUploadErrorMsg()) {
                          <div class="fm-upload-error" role="alert">
                            <span>&#9888;</span> {{ audioUploadErrorMsg() }}
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="col-sm-6">
                        <button type="button" class="fm-common-btn fm-secondary-btn fm-secondary-border-btn fm-large-btn w-100"
                                (click)="retakeAudioRecording()">
                          <img src="images/icons/fm-reset.svg" class="fm-svg" alt="Retake" />
                          Retake
                        </button>
                      </div>
                      <div class="col-sm-6">
                        <button type="button" class="fm-common-btn fm-record-btn fm-large-btn w-100 fm-btn-recording"
                                (click)="stopRecording()">
                          <img src="images/icons/fm-recording-dot.svg" class="fm-svg" alt="Stop" />
                          Stop Recording
                        </button>
                      </div>
                    }
                  </div>
                </div>
                @if (permissionError() && mediaTypeStr() === 'audio') {
                  <div class="alert alert-danger mt-3 mb-0" role="alert">
                    <strong>{{ permissionError() }}</strong>
                    @if (isPermissionDenied()) {
                      <div class="mt-2">
                        Set Microphone to <strong>Allow</strong>, then reload the page and try again.
                      </div>
                    }
                  </div>
                }
              </div>
            }

            <!-- ============================================================ -->
            <!-- IMAGE + AUDIO RECORDING                                       -->
            <!-- ============================================================ -->
            @if (mediaTypeStr() === 'image-audio') {
              <div class="fm-audio-box">
                <div class="fm-audio-preview" [attr.data-recording-state]="recordingState()"
                     [class.fm-drag-over]="imageDragOver()"
                     (dragover)="onImageAreaDragOver($event)"
                     (dragleave)="onImageAreaDragLeave($event)"
                     (drop)="onImageAreaDrop($event)">
                  @if (!imagePreviewUrl()) {
                    <label for="spImageAudioInput" class="fm-audio-icon fm-audio-upload-label" style="cursor:pointer">
                      <img src="images/icons/fm-upload.svg" class="fm-svg" alt="Upload Photo" />
                    </label>
                    <p class="mb-0 mt-2">Drop photo here, or
                      <span class="fm-browse-link" (click)="imageAudioInputRef()?.nativeElement?.click()">Browse</span>
                    </p>
                    <input type="file" #imageAudioInput id="spImageAudioInput" class="d-none"
                           accept="image/jpeg,image/png,image/webp,image/gif,image/*"
                           (change)="onImageForAudioUpload($event)" />
                  } @else {
                    <div class="fm-img-preview-block">
                      <div class="fm-img-preview-thumb-wrap">
                        <img [src]="imagePreviewUrl()" class="fm-img-preview-thumb" alt="Uploaded Photo" />
                        <button type="button" class="fm-img-remove-corner" (click)="removeImageForAudio()"
                                title="Remove image" aria-label="Remove image">
                          <i class="bi bi-x"></i>
                        </button>
                      </div>
                    </div>
                    <input type="file" id="spImageAudioInput" class="d-none"
                           accept="image/jpeg,image/png,image/webp,image/gif,image/*"
                           (change)="onImageForAudioUpload($event)" />
                  }
                  <div class="fm-audio-waveform mt-3" aria-hidden="true">
                    @if (recordingState() === 'recording') {
                      <div class="fm-audio-visualizer" aria-hidden="true">
                        <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                        <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                        <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                        <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                        <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                        <span class="fm-av-bar"></span><span class="fm-av-bar"></span>
                      </div>
                      <div class="fm-audio-rec-row mt-2">
                        <span class="fm-rec-dot" aria-hidden="true"></span>
                        <p class="fm-record-status mb-0" aria-live="polite">
                          Recording... {{ formatRecordingTime(recordingTimer()) }}
                        </p>
                      </div>
                    } @else {
                      <img src="images/icons/fm-audio-wave.svg" class="fm-audio-wave-img" alt="Wave" />
                    }
                  </div>
                  <audio #audioPreview class="fm-audio-player w-100 mt-3"
                         [class.d-none]="recordingState() !== 'stopped' || (!recordedUrl() && !uploadedUrl())"
                         controls></audio>
                </div>
                @if (imageUploadErrorMsg()) {
                  <p class="fm-upload-error mt-2">{{ imageUploadErrorMsg() }}</p>
                }
                <div class="fm-audio-btn">
                  <div class="row g-3 g-xl-4">
                    @if (recordingState() !== 'recording') {
                      <div class="col-sm-6">
                        <button type="button" class="fm-common-btn fm-record-btn fm-large-btn w-100"
                                (click)="startAudioRecording()">
                          <img src="images/icons/fm-record.svg" class="fm-svg" alt="Record" />
                          {{ (recordedUrl() || uploadedUrl()) ? 'Re-record Audio' : 'Record Audio' }}
                        </button>
                      </div>
                      <div class="col-sm-6">
                        <label for="spAudioUploadImageAudio"
                               class="fm-common-btn fm-secondary-btn fm-secondary-border-btn fm-upload-btn fm-large-btn w-100"
                               style="cursor:pointer">
                          <img src="images/icons/fm-upload.svg" class="fm-svg" alt="Upload" />
                          Upload Audio
                        </label>
                        <input type="file" id="spAudioUploadImageAudio" class="d-none" accept="audio/*"
                               (change)="onAudioUpload($event)" />
                        @if (audioUploadErrorMsg()) {
                          <div class="fm-upload-error" role="alert">
                            <span>&#9888;</span> {{ audioUploadErrorMsg() }}
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="col-sm-6">
                        <button type="button" class="fm-common-btn fm-secondary-btn fm-secondary-border-btn fm-large-btn w-100"
                                (click)="retakeAudioRecording()">
                          <img src="images/icons/fm-reset.svg" class="fm-svg" alt="Retake" />
                          Retake
                        </button>
                      </div>
                      <div class="col-sm-6">
                        <button type="button" class="fm-common-btn fm-record-btn fm-large-btn w-100 fm-btn-recording"
                                (click)="stopRecording()">
                          <img src="images/icons/fm-recording-dot.svg" class="fm-svg" alt="Stop" />
                          Stop Recording
                        </button>
                      </div>
                    }
                  </div>
                </div>
                @if (permissionError() && mediaTypeStr() === 'image-audio') {
                  <div class="alert alert-danger mt-3 mb-0" role="alert">
                    <strong>{{ permissionError() }}</strong>
                    @if (isPermissionDenied()) {
                      <div class="mt-2">
                        Set Microphone to <strong>Allow</strong>, then reload the page and try again.
                      </div>
                    }
                  </div>
                }
              </div>
            }

            <!-- ============================================================ -->
            <!-- Cannot proceed warning                                        -->
            <!-- ============================================================ -->
            @if (!canProceed()) {
              <div class="alert alert-warning mt-3 mb-0 d-flex align-items-center gap-2" role="alert">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true" class="flex-shrink-0">
                  <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                </svg>
                @if (mediaTypeStr() === 'video') {
                  <span>Please <strong>start recording</strong> or <strong>upload a video</strong> to continue.</span>
                } @else if (mediaTypeStr() === 'audio') {
                  <span>Please <strong>start recording</strong> or <strong>upload an audio file</strong> to continue.</span>
                } @else {
                  <span>Please <strong>upload an image</strong> and <strong>record audio</strong> to continue.</span>
                }
              </div>
            }

            <!-- Footer nav -->
            <div class="fm-form-footer fm-space-between mt-4">
              <span></span>
              @if (questionsList().length > 1 && !isLastQuestion() && !allAnsweredByUpload()) {
                <button type="button" class="fm-common-btn fm-secondary-btn fm-white-btn-txt"
                        [disabled]="recordingState() !== 'recording'"
                        (click)="nextQuestion()">
                  Next Question
                  <img src="images/icons/fm-arrow-right.svg" class="fm-svg" alt="Next" />
                </button>
              } @else {
                <button type="button" class="fm-common-btn fm-secondary-btn fm-white-btn-txt"
                        [disabled]="!canProceed()"
                        (click)="goToReview()">
                  Review &amp; Submit
                  <img src="images/icons/fm-arrow-right.svg" class="fm-svg" alt="Next" />
                </button>
              }
            </div>

          </div>
        </div>

        <!-- Sidebar: Questions List + Recording Tips -->
        <div class="col-lg-4">
          <div class="row g-4">

            @if (questionsList().length > 1) {
              <div class="col-12">
                <div class="fm-sidebar-card fm-question-list-accordion">
                  <div class="fm-sidebar-title">
                    <span class="fm-head-20">Questions List</span>
                  </div>
                  <div class="fm-question-list">
                    @for (q of questionsList(); track q.questionId; let i = $index) {
                      <div class="fm-question-card"
                           [class.active]="i === currentQuestionIndex()"
                           [class.fm-answered]="isQuestionAnswered(q.questionId)">
                        <span class="fm-question-number">{{ (i + 1).toString().padStart(2, '0') }}</span>
                        <p class="mb-0 fm-question-text">{{ q.questionText }}</p>
                        @if (questionTimeframes()[q.questionId]) {
                          <span class="fm-timeframe-badge">
                            {{ formatRecordingTime(questionTimeframes()[q.questionId].startSecond) }} -
                            {{ formatRecordingTime(questionTimeframes()[q.questionId].endSecond) }}
                          </span>
                        }
                        <div class="fm-check-icon" aria-hidden="true">
                          <img src="images/icons/fm-check-white.svg" class="fm-svg" alt="" />
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>
            }

            <div class="col-12">
              <div class="fm-sidebar-card">
                <div class="fm-sidebar-title">
                  <h5 class="fm-head-20">Recording Tips</h5>
                </div>
                <div class="fm-recording-tips-list">
                  <div class="fm-detail-item">
                    <div class="fm-detail-icon"><img class="fm-svg" src="images/icons/fm-speak-sm.svg" alt="Tip" /></div>
                    <div class="fm-detail-content"><p class="fm-detail-text">Speak naturally, as if talking to a friend</p></div>
                  </div>
                  <div class="fm-detail-item">
                    <div class="fm-detail-icon"><img class="fm-svg" src="images/icons/fm-clock-sm.svg" alt="Tip" /></div>
                    <div class="fm-detail-content"><p class="fm-detail-text">Take your time &amp; pause when needed</p></div>
                  </div>
                  <div class="fm-detail-item">
                    <div class="fm-detail-icon"><img class="fm-svg" src="images/icons/fm-check-sm.svg" alt="Tip" /></div>
                    <div class="fm-detail-content"><p class="fm-detail-text">Include specific details and emotions</p></div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  }

  <!-- Reload Warning Modal -->
  @if (showReloadWarning()) {
    <div class="fm-reload-overlay" role="dialog" aria-modal="true" aria-labelledby="spReloadModalTitle">
      <div class="fm-reload-modal">
        <div class="fm-reload-modal-icon"><span>&#9888;</span></div>
        <h3 class="fm-reload-modal-title" id="spReloadModalTitle">Leave this page?</h3>
        <p class="fm-reload-modal-desc">Your recording will be <strong>permanently lost</strong> if you refresh or leave.</p>
        <div class="fm-reload-modal-actions">
          <button type="button" class="fm-common-btn fm-primary-btn" (click)="dismissReloadWarning()">Stay &amp; Continue</button>
          <button type="button" class="fm-reload-leave-btn" (click)="confirmReload()">Leave anyway</button>
        </div>
      </div>
    </div>
  }

</div>
